import { ColumnDef } from '@tanstack/react-table';
import ChevronDownIcon from '../../assets/icons/chevron_down.svg?react';
import EditIcon from '../../assets/icons/edit.svg?react';
import GraphIcon from '../../assets/icons/graph.svg?react';
import NotificationIcon from '../../assets/icons/notification.svg?react';
import TrashIcon from '../../assets/icons/trash.svg?react';
import { MachineBadge } from '../../components/MachineBadge';
import { TableRowActions } from '../../components/tables/TableRowActions';
import { Toggle } from '../../components/Toggle';
import { Alarm } from '../../gql/generated';
import { buildAlarmDetailUrl } from '../../utils/alarmDetailUrl';
import { cn } from '../../utils/className';
import { machineDisplayName } from '../../utils/machineDisplay';
import {
  formatMetricName,
  formatThreshold,
  formatTrigger,
} from '../../utils/formatMetric';
import {
  AlarmTableRow,
  alarmRowAlarms,
  alarmRowMachines,
  alarmRowName,
} from './alarmRows';

interface AlarmColumnsOptions {
  onToggleEnabled: (row: AlarmTableRow) => Promise<void>;
  onEdit: (row: AlarmTableRow) => void;
  onDelete: (row: AlarmTableRow) => void;
  onTestNotification: (row: AlarmTableRow) => Promise<void>;
  /** Group ids with a test notification in flight. */
  testingGroupIds: Set<string>;
  /** Whether the row's alert belongs to the signed-in user. See canEditAlarmRow. */
  canEdit: (row: AlarmTableRow) => boolean;
}

/**
 * A group row summarises its signals as a count. One signal reads as itself,
 * so a group of one still says what it watches.
 */
function renderSummary(
  row: AlarmTableRow,
  countLabel: string,
  describe: (alarm: Alarm) => string
) {
  const alarms = alarmRowAlarms(row);
  const text =
    alarms.length > 1 ? `${alarms.length} ${countLabel}` : describe(alarms[0]);

  return (
    <div className='truncate' title={text}>
      {text}
    </div>
  );
}

export function createAlarmColumns({
  onToggleEnabled,
  onEdit,
  onDelete,
  onTestNotification,
  testingGroupIds,
  canEdit,
}: AlarmColumnsOptions): ColumnDef<AlarmTableRow>[] {
  return [
    {
      id: 'alarmName',
      accessorFn: (row) => alarmRowName(row),
      header: 'Alert name',
      cell: ({ row }) => {
        const name = alarmRowName(row.original);
        const isExpanded = row.getIsExpanded();
        // A member row drops the group name it is shown under, so the title
        // carries the name the alert is stored and searched under
        const storedName = row.original.group
          ? row.original.group.name
          : row.original.alarm.name;

        return (
          <div className='flex min-w-0 items-center'>
            {/* The expander sits in the gutter the column reserves, so every
                name starts on the column's content edge. The 24px pull equals
                the 12px icon plus the 12px gap that follows it. */}
            <span
              className='mr-3 -ml-6 flex w-3 shrink-0 items-center
                justify-center'
            >
              {row.getCanExpand() && (
                <button
                  type='button'
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    row.toggleExpanded();
                  }}
                  // The icon carries a fixed blue fill, so paint it from the
                  // text colour instead
                  // The pseudo-element widens the click target. The icon
                  // keeps its 12px size.
                  className="text-secondary hover:text-primary relative flex
                    cursor-pointer items-center justify-center after:absolute
                    after:-inset-2.5 after:content-[''] [&_path]:fill-current"
                >
                  <ChevronDownIcon
                    width={12}
                    height={12}
                    className={cn(
                      'transition-transform',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </button>
              )}
            </span>
            <span
              className='hover:text-focus cursor-pointer truncate'
              title={storedName}
            >
              {name}
            </span>
          </div>
        );
      },
      enableSorting: true,
      enableHiding: false,
      enableResizing: true,
      size: 200,
      // Room for the expander, with the same gap on both sides of it
      meta: { paddingLeft: 36 },
    },
    {
      id: 'machine',
      header: 'Machines',
      cell: ({ row }) => {
        const machines = alarmRowMachines(row.original);
        if (machines.length === 0) {
          return (
            <span className='size-caption text-disabled'>No machines</span>
          );
        }

        const machineNames = machines.map(machineDisplayName);
        const displayText =
          machines.length === 1
            ? machineNames[0]
            : `${machines.length} machines`;

        // The count badge lists the names on hover
        return (
          <div className='flex items-center' title={machineNames.join(', ')}>
            <MachineBadge name={displayText} />
          </div>
        );
      },
      enableSorting: false,
      enableResizing: true,
      size: 160,
    },
    {
      id: 'signal',
      accessorFn: (row) => row.alarm.signal,
      header: 'Signal',
      cell: ({ row }) =>
        renderSummary(row.original, 'signals', (alarm) =>
          formatMetricName(alarm.signal)
        ),
      enableSorting: true,
      enableResizing: true,
      size: 140,
    },
    {
      id: 'threshold',
      header: 'Threshold',
      cell: ({ row }) =>
        renderSummary(row.original, 'thresholds', (alarm) =>
          formatThreshold(alarm)
        ),
      enableSorting: false,
      enableResizing: true,
      size: 140,
    },
    {
      id: 'trigger',
      header: 'Trigger',
      cell: ({ row }) => renderSummary(row.original, 'triggers', formatTrigger),
      enableSorting: false,
      enableResizing: true,
      size: 160,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const tableRow = row.original;
        const alarms = alarmRowAlarms(tableRow);
        const name = alarmRowName(tableRow);
        const detailUrl =
          alarms.length === 1 ? buildAlarmDetailUrl(alarms[0]) : undefined;
        // Another user's alert can be looked at and deleted, not changed
        const editable = canEdit(tableRow);
        const group = tableRow.group;
        const isTesting = group ? testingGroupIds.has(group.id) : false;

        const actions = [
          // One dashboard shows one signal, so a multi-signal alert has
          // no detail view
          ...(alarms.length > 1
            ? []
            : [
                {
                  label: 'View Detail',
                  onClick: () => {
                    if (detailUrl) {
                      window.open(detailUrl, '_blank', 'noopener,noreferrer');
                    }
                  },
                  icon: <GraphIcon />,
                  variant: 'default' as const,
                  disabled: !detailUrl,
                },
              ]),
          ...(editable
            ? [
                {
                  label: 'Edit Pulse alert',
                  onClick: () => onEdit(tableRow),
                  icon: <EditIcon />,
                  variant: 'default' as const,
                },
                // EDMS delivers through the group's channels, so only a row
                // that carries a group has a delivery path to test. A legacy
                // alarm without one has none.
                ...(group
                  ? [
                      {
                        label: isTesting
                          ? 'Sending...'
                          : 'Send test notification',
                        onClick: () => {
                          void onTestNotification(tableRow);
                        },
                        icon: <NotificationIcon />,
                        variant: 'default' as const,
                        disabled: isTesting,
                      },
                    ]
                  : []),
              ]
            : []),
          {
            label: 'Delete Pulse alert',
            onClick: () => onDelete(tableRow),
            icon: <TrashIcon />,
            variant: 'danger' as const,
            separator: true,
          },
        ];

        return (
          <div className='flex w-full items-center justify-end gap-3'>
            {/* One toggle per alert, on the top-level row. A grouped alert
                switches as a whole, so its member rows carry none. */}
            {row.depth === 0 && editable && (
              <div onClick={(e) => e.stopPropagation()}>
                <Toggle
                  checked={alarms.every((alarm) => alarm.isEnabled)}
                  onCheckedChange={() => onToggleEnabled(tableRow)}
                  aria-label={`Toggle ${name}`}
                />
              </div>
            )}
            {actions.length > 0 && <TableRowActions actions={actions} />}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
      enableResizing: false,
      enablePinning: true,
      size: 100,
      minSize: 100,
    },
  ];
}
